import React, { useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  ShoppingBag,
  Users,
  Settings,
  ChevronDown,
  Menu,
  X,
  LogOut,
  Home,
  FileText,
  ImageIcon,
  MessageSquare,
  Tag,
  Star,
} from 'lucide-react';
import { Button } from '../../components/ui/button';

const AdminLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [expandedMenus, setExpandedMenus] = useState(['products']);

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    navigate('/');
    window.location.reload();
  };

  const toggleMenu = (menu) => {
    setExpandedMenus((prev) =>
      prev.includes(menu) ? prev.filter((m) => m !== menu) : [...prev, menu]
    );
  };

  const menuItems = [
    { path: '/admin', label: 'Dashboard', icon: LayoutDashboard },
    {
      label: 'Products',
      icon: Package,
      submenu: [
        { path: '/admin/products', label: 'All Products' },
        { path: '/admin/categories', label: 'Categories' },
        { path: '/admin/brands', label: 'Brands' },
      ],
    },
    {
      label: 'Pages',
      icon: FileText,
      submenu: [
        { path: '/admin/pages', label: 'All Pages' },
        { path: '/admin/page-categories', label: 'Categories' },
        { path: '/admin/sliders', label: 'Sliders' },
      ],
    },
    { path: '/admin/media', label: 'Media Gallery', icon: ImageIcon },
    { path: '/admin/orders', label: 'Orders', icon: ShoppingBag },
    { path: '/admin/customers', label: 'Customers', icon: Users },
    { path: '/admin/support', label: 'Support', icon: MessageSquare },
    { path: '/admin/coupons', label: 'Coupons', icon: Tag },
    { path: '/admin/reviews', label: 'Reviews', icon: Star },
    {
      label: 'Settings',
      icon: Settings,
      submenu: [
        { path: '/admin/settings/general', label: 'General' },
        { path: '/admin/settings/locations', label: 'Locations' },
        { path: '/admin/settings/shipping', label: 'Shipping (Envía)' },
        { path: '/admin/settings/warehouses', label: 'Warehouses' },
        { path: '/admin/settings/access', label: 'Access & API Keys' },
      ],
    },
  ];

  const isActive = (path) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(path);
  };

  const isMenuExpanded = (label) => expandedMenus.includes(label.toLowerCase());

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <aside
        className={`bg-[#2C2420] text-white transition-all duration-300 ${
          isSidebarOpen ? 'w-64' : 'w-20'
        } flex flex-col`}
      >
        {/* Logo */}
        <div className="p-6 border-b border-gray-700">
          <div className="flex items-center justify-between">
            {isSidebarOpen ? (
              <img 
                src="/logo-cimonamia.png" 
                alt="Cimonamia Logo" 
                className="h-8 w-auto"
              />
            ) : (
              <img 
                src="/logo-cimonamia.png" 
                alt="Cimonamia Logo" 
                className="h-6 w-auto"
              />
            )}
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="text-gray-400 hover:text-white lg:block hidden"
            >
              {isSidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
          {isSidebarOpen && (
            <p className="text-xs text-gray-400 mt-1">Admin Panel</p>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 overflow-y-auto">
          <div className="space-y-1">
            {menuItems.map((item) => {
              if (item.submenu) {
                const isExpanded = isMenuExpanded(item.label);
                return (
                  <div key={item.label}>
                    <button
                      onClick={() => toggleMenu(item.label.toLowerCase())}
                      className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-gray-300 hover:bg-gray-700 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <item.icon className="h-5 w-5" />
                        {isSidebarOpen && <span>{item.label}</span>}
                      </div>
                      {isSidebarOpen && (
                        <ChevronDown
                          className={`h-4 w-4 transition-transform ${
                            isExpanded ? 'rotate-180' : ''
                          }`}
                        />
                      )}
                    </button>
                    {isExpanded && isSidebarOpen && (
                      <div className="ml-8 mt-1 space-y-1">
                        {item.submenu.map((subItem) => (
                          <Link
                            key={subItem.path}
                            to={subItem.path}
                            className={`block px-4 py-2 rounded-lg text-sm ${
                              isActive(subItem.path)
                                ? 'bg-[#be1f1a] text-white'
                                : 'text-gray-400 hover:bg-gray-700'
                            }`}
                          >
                            {subItem.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive(item.path)
                      ? 'bg-[#be1f1a] text-white'
                      : 'text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  {isSidebarOpen && <span>{item.label}</span>}
                </Link>
              );
            })}
          </div>
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-gray-700">
          <Link
            to="/"
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-300 hover:bg-gray-700 transition-colors mb-2"
          >
            <Home className="h-5 w-5" />
            {isSidebarOpen && <span>Back to Store</span>}
          </Link>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-gray-300 hover:bg-gray-700 transition-colors"
          >
            <LogOut className="h-5 w-5" />
            {isSidebarOpen && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <header className="bg-white shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-800">
                {menuItems.find((item) => isActive(item.path))?.label || 'Admin Panel'}
              </h2>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">
                {localStorage.getItem('userEmail') || 'Admin'}
              </span>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;